import { isoDay, TIME_ZONE } from './time.ts';

// WhatsApp (sinds 2.3.1, ticket 33235): het eigen nummer zoals de dienst het wil, en de foutcodes en stopcodes van de dienst in
// gewone taal. De dienst jerome-whatsapp schrijft in wa_account alleen codes (last_error, halted), nooit tekst met gegevens; Jerome 2
// vertaalt ze hier, voor het scherm, de meldingen en de mail. Een code kan een deel na een dubbele punt hebben:
// `temp_ban:<code>:<einde>` (einde een ISO-tijd in UTC of leeg) en `connect_failed:<n>`.

/** De zin bij een nummer dat niet klopt (route en scherm gebruiken dezelfde). */
export const WA_NUMBER_ERROR = 'Dat nummer klopt niet. Vul het in met landcode, zonder plusteken of nullen ervoor.';

/** Het nummer zoals de dienst het wil: spaties, plustekens, streepjes en haakjes eruit, daarna 8 tot 15 cijfers die niet met 0
 * beginnen. Null als het daarna geen internationaal nummer is. */
export function waNumberOf(raw: string): string | null {
  const digits = raw.replace(/[\s+\-()]/g, '');
  return /^[1-9][0-9]{7,14}$/.test(digits) ? digits : null;
}

/** Het deel van een code vóór de eerste dubbele punt (`temp_ban:101:…` geeft `temp_ban`). */
export function waCodeBase(code: string): string {
  const i = code.indexOf(':');
  return i < 0 ? code : code.slice(0, i);
}

/** Media uit WhatsApp (sinds 2.3.5): tot deze grootte haalt de dienst een bestand vanzelf op; groter alleen op verzoek, tot
 * WA_MEDIA_LARGE_BYTES (dezelfde grenzen als MaxAuto en MaxLarge in whatsapp/internal/media). */
export const WA_MEDIA_AUTO_BYTES = 25 * 1024 * 1024;
export const WA_MEDIA_LARGE_BYTES = 100 * 1024 * 1024;

/** De stopcodes: na zo'n stop verbindt de dienst pas weer op de opdracht verbinden (knop Opnieuw verbinden). */
export const WA_HALT_CODES = ['client_outdated', 'stream_replaced', 'connect_failed'] as const;

const FIXED: Record<string, string> = {
  pair_budget: 'Je hebt vandaag al drie keer geprobeerd te koppelen. Morgen kan het weer.',
  pair_expired: 'De code is verlopen voordat je telefoon hem gebruikte. Vraag een nieuwe aan.',
  pair_failed: 'Koppelen is niet gelukt. Probeer het opnieuw.',
  pair_wrong_account: 'Er werd gekoppeld met een ander WhatsApp-account dan je eigen nummer. Jerome heeft dat afgebroken.',
  pair_interrupted: 'Het koppelen is onderbroken omdat de WhatsApp-dienst opnieuw startte. Probeer het opnieuw.',
  pair_number: WA_NUMBER_ERROR,
  pair_passkey: 'Je telefoon vraagt om een passkey; dat kan Jerome niet. Probeer de QR-code.',
  already_linked: 'WhatsApp is al gekoppeld.',
  logged_out: 'WhatsApp is ontkoppeld, op je telefoon of omdat je telefoon veertien dagen niet gebruikt is. Koppel opnieuw; het archief blijft bewaard.',
  main_device_gone: 'WhatsApp heeft de koppeling beëindigd, bijvoorbeeld omdat je account vergrendeld is of je een andere telefoon gebruikt. Koppel opnieuw; het archief blijft bewaard.',
  banned: 'WhatsApp heeft je account geblokkeerd. Vraag in de app op je telefoon om een beoordeling. Jerome verbindt niet meer.',
  client_outdated: 'WhatsApp weigert deze versie van de WhatsApp-dienst. Jerome is gestopt; na een update van de dienst kun je opnieuw verbinden.',
  stream_replaced: 'Een andere verbinding met dezelfde koppeling nam het over. Jerome is gestopt en verbindt niet vanzelf opnieuw.',
  session_missing: 'De sleutels van de koppeling ontbreken. Koppel opnieuw.',
  handler_panic: 'Een bericht kon niet verwerkt worden; de dienst draait door.',
  store_failed: 'Opslaan in het archief lukte even niet; WhatsApp levert het bericht opnieuw.',
  store_dropped: 'Een bericht of wijziging paste niet in het archief en is overgeslagen; de rest staat er wel in.',
  unlink_remote_failed: 'De sleutels zijn gewist, maar WhatsApp kreeg het ontkoppelen niet door. Verwijder Jerome ook op je telefoon onder Gekoppelde apparaten.',
};

const dayFmt = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', timeZone: TIME_ZONE });
const timeFmt = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: TIME_ZONE });

/** Een moment in Amsterdamse tijd voor in een zin: vandaag "15:30", morgen "morgen 15:30", anders "dinsdag 10 november 15:30". */
export function waMoment(at: Date, now: Date): string {
  const time = timeFmt.format(at);
  const day = isoDay(at);
  if (day === isoDay(now)) return time;
  if (day === isoDay(new Date(now.getTime() + 86_400_000))) return `morgen ${time}`;
  return `${dayFmt.format(at)} ${time}`;
}

/** Een code in beeld alleen met letters, cijfers en een paar leestekens, en kort: de dienst schrijft codes, maar wat in de
 * database staat, komt ongezien op het scherm en in de mail. */
function safe(part: string, max: number): string {
  return part.replace(/[^A-Za-z0-9_.:-]/g, '').slice(0, max);
}

/**
 * Een fout- of stopcode van de dienst in gewone taal ('' voor geen code). `now` bepaalt hoe het einde van een tijdelijke blokkade
 * heet (vandaag alleen de tijd). Een onbekende code: "Foutcode <code>.".
 */
export function waErrorText(code: string, now: Date = new Date()): string {
  const c = code.trim();
  if (!c) return '';
  const base = waCodeBase(c);
  if (base === 'temp_ban') {
    // temp_ban:<code>:<einde>; het einde is een ISO-tijd met dubbele punten, dus alles na de tweede dubbele punt.
    const rest = c.slice('temp_ban:'.length);
    const i = rest.indexOf(':');
    const endRaw = i < 0 ? '' : rest.slice(i + 1);
    const end = endRaw ? new Date(endRaw) : null;
    const until = end && !Number.isNaN(end.getTime()) ? ` (tot ${waMoment(end, now)})` : '';
    return `WhatsApp heeft je account tijdelijk geblokkeerd${until}. Jerome is gestopt en verbindt niet vanzelf opnieuw. Ontkoppel Jerome op je telefoon; daarna beslis jij of het weer aangaat.`;
  }
  if (base === 'connect_failed') {
    const n = safe(c.slice('connect_failed'.length + 1), 12);
    return `WhatsApp weigerde de verbinding${n ? ` (code ${n})` : ''}. Jerome is gestopt.`;
  }
  return FIXED[c] ?? `Foutcode ${safe(c, 60)}.`;
}

// ---- Bewaking van de telefoon (sinds 2.3.6, ticket 33235 punt 5) ----
// WhatsApp verbreekt een gekoppeld apparaat als de telefoon ongeveer veertien dagen niet online was. De dienst zet phone_seen_at bij
// elk live bericht, elke reactie of wijziging die de eigenaar op zijn telefoon verstuurt (apparaatnummer 0, alleen vooruit); lezen geeft
// geen teken. Na tien dagen zonder teken krijgt de eigenaar een melding, op dag dertien één herinnering, en de teksten zeggen alleen wat
// Jerome weet: geen teken gezien. Alleen als de dienst verbonden is, want anders kan hij geen teken zien. Koppelen gaat via de telefoon,
// dus een koppeling die nieuwer is dan het laatste teken telt ook als teken.

export const WA_PHONE_QUIET_DAYS = 10;
export const WA_PHONE_REMIND_DAYS = 13;
export const WA_PHONE_UNLINK_DAYS = 14;
const DAY_MS = 86_400_000;

/** Een telefoon die te lang geen teken gaf: de soort melding, de hele dagen sinds het laatste teken, dat moment (ISO, de sleutel van het
 * voorval) en of er sinds het koppelen nog geen teken was. */
export interface WaPhoneQuiet {
  stage: 'phone_quiet' | 'phone_remind';
  days: number;
  since: string;
  never: boolean;
}

const at = (iso: string | null): number => (iso ? Date.parse(iso) : Number.NaN);

/** De stille telefoon, of null: alleen als WhatsApp gekoppeld is, de dienst verbonden is en het laatste teken (of de koppeling, als die
 * nieuwer is of er geen teken is) minstens tien dagen oud is. */
export function waPhoneQuiet(s: { state: string; connected: boolean; phoneSeenAt: string | null; linkedAt: string | null }, now: Date): WaPhoneQuiet | null {
  if (s.state !== 'linked' || !s.connected) return null;
  const seen = at(s.phoneSeenAt);
  const linked = at(s.linkedAt);
  const useLinked = Number.isFinite(linked) && (!Number.isFinite(seen) || linked > seen);
  const t = useLinked ? linked : seen;
  if (!Number.isFinite(t)) return null;
  const days = Math.floor((now.getTime() - t) / DAY_MS);
  if (days < WA_PHONE_QUIET_DAYS) return null;
  return { stage: days >= WA_PHONE_REMIND_DAYS ? 'phone_remind' : 'phone_quiet', days, since: (useLinked ? s.linkedAt : s.phoneSeenAt)!, never: !Number.isFinite(seen) };
}

const WORDS = ['nul', 'een', 'twee', 'drie', 'vier', 'vijf', 'zes', 'zeven', 'acht', 'negen', 'tien', 'elf', 'twaalf', 'dertien', 'veertien'];

/** Een aantal dagen in woorden tot veertien ("tien dagen", "een dag"), daarboven in cijfers. */
export function waDays(n: number): string {
  const word = WORDS[n] ?? String(n);
  return n === 1 ? 'een dag' : `${word} dagen`;
}

/** Hoe lang geleden in een zin ("zojuist", "5 minuten geleden", "3 uur geleden", "elf dagen geleden"); '' bij een onleesbare tijd. */
export function waAgo(iso: string | null, now: Date): string {
  const t = at(iso);
  if (!Number.isFinite(t)) return '';
  const ms = Math.max(0, now.getTime() - t);
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'zojuist';
  if (min < 60) return min === 1 ? 'een minuut geleden' : `${min} minuten geleden`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours} uur geleden`;
  return `${waDays(Math.floor(ms / DAY_MS))} geleden`;
}

const PHONE_ACTION = 'Open WhatsApp op je telefoon, met internet, dan blijft de koppeling staan.';
const PHONE_RULE = `Was je telefoon al die tijd niet online, dan verbreekt WhatsApp de koppeling met Jerome na ${waDays(WA_PHONE_UNLINK_DAYS)}`;

/** De titel van de melding over een stille telefoon (de app toont titel en tekst achter elkaar, dus niet de eerste zin van de tekst). */
export function waPhoneTitle(q: WaPhoneQuiet): string {
  return q.stage === 'phone_remind' ? 'Over ongeveer een dag kan WhatsApp de koppeling verbreken' : 'Let op je WhatsApp-koppeling';
}

/** De uitleg bij de melding, in de dagregel en onder de rij "Laatste teken van je telefoon" (whatsapp-telefoon-uitleg): hoe lang Jerome
 * geen teken zag, wat WhatsApp na veertien dagen doet en wat de eigenaar doet. */
export function waPhoneText(q: WaPhoneQuiet): string {
  const how = q.never ? `sinds het koppelen, ${waDays(q.days)} geleden,` : `${waDays(q.days)} lang`;
  const when = q.stage === 'phone_remind' ? (q.days >= WA_PHONE_UNLINK_DAYS ? ', dus dat kan elk moment gebeuren' : ', dus misschien morgen al') : '';
  return `Jerome zag ${how} geen teken van je telefoon in WhatsApp, zoals een bericht of reactie die je op je telefoon verstuurde. ${PHONE_RULE}${when}. ${PHONE_ACTION}`;
}
